/**
 * How an om_files row relates to the actual bytes in SharePoint.
 *
 * uploadOMFile writes the DB row first and uploads second, so the row can
 * outlive a failed upload: sharepoint_item_id stays null and the file browser
 * used to list it as an ordinary file whose download could only ever fail.
 * This module is the single place that decides what a row's null item id
 * means, so the service and the UI can't drift apart on it.
 *
 * Pure on purpose — no Supabase, no Graph, no clock (callers inject `nowMs`).
 */

export const FOLDER_MARKER_NAME = '.folder-marker.txt';

/**
 * How long a row may sit with no item id before we stop calling it "syncing".
 * An 11MB OM over a slow connection is minutes, not seconds; but if the tab
 * closed mid-upload no catch ever ran to flag the row, and it would otherwise
 * read as in-flight forever.
 */
export const STALE_SYNC_MS = 10 * 60 * 1000;

export type OMFileSyncState = 'synced' | 'syncing' | 'failed' | 'folder-marker';

/** The subset of an OMFile row this module needs. */
export interface OMFileSyncFields {
  file_name: string;
  sharepoint_item_id?: string | null;
  upload_failed?: boolean | null;
  upload_error?: string | null;
  created_at?: string | null;
}

export function omFileSyncState(file: OMFileSyncFields, nowMs: number = Date.now()): OMFileSyncState {
  if (file.file_name === FOLDER_MARKER_NAME) return 'folder-marker';

  // The item id is ground truth: if it's there the file downloads, whatever a
  // leftover flag from an earlier attempt says.
  if (file.sharepoint_item_id?.trim()) return 'synced';

  if (file.upload_failed) return 'failed';

  const createdAt = file.created_at ? Date.parse(file.created_at) : NaN;
  if (Number.isNaN(createdAt)) return 'failed';

  return nowMs - createdAt < STALE_SYNC_MS ? 'syncing' : 'failed';
}

export function isOMFileDownloadable(file: OMFileSyncFields, nowMs: number = Date.now()): boolean {
  return omFileSyncState(file, nowMs) === 'synced';
}

/** User-facing explanation of why a file isn't available, or null if it is. */
export function omFileSyncMessage(file: OMFileSyncFields, nowMs: number = Date.now()): string | null {
  switch (omFileSyncState(file, nowMs)) {
    case 'failed':
      return file.upload_error?.trim()
        ? `Upload to SharePoint failed: ${file.upload_error.trim()} Retry to send this file again.`
        : 'Upload to SharePoint never finished. Retry to send this file again.';
    case 'syncing':
      return 'Still uploading to SharePoint…';
    default:
      return null;
  }
}
